import { useId } from "react"
import StatsCard from "./cards/stats-card"
import { Button } from "@workspace/web-ui/components/button"
import { cn } from "@workspace/web-ui/lib/utils"
import { DashboardStatsCardProps } from "../types/prop.types"
import { useNavigate, useParams, useRouterState } from "@tanstack/react-router"
import { todosQueryOptions } from "../api/queries"
import { useQuery } from "@tanstack/react-query"
import { getApiProgram } from "~/lib/utils"
import { Skeleton } from "@workspace/web-ui/components/skeleton"
import { QUICK_ACTIONS_POLLING_INTERVAL } from "../types/constant"

interface HomeStatsProps {
    className?: string;
    // stats: DashboardStatsCardProps[];
}

export const QuickActionCardSkeleton = () => {
    return (
        <div className="flex flex-col sm:flex-row gap-4 md:gap-6 w-full">
            <Skeleton className="w-full h-[100px] border border-[#E6E6E6] rounded-md animate-pulse bg-white shadow-sm" />
            <Skeleton className="w-full h-[100px] border border-[#E6E6E6] rounded-md animate-pulse bg-white shadow-sm" />
            <Skeleton className="w-full h-[100px] border border-[#E6E6E6] rounded-md animate-pulse bg-white shadow-sm" />
            <Skeleton className="w-full h-[100px] border border-[#E6E6E6] rounded-md animate-pulse bg-white shadow-sm" />
        </div>
    )
}

const HomeStats = ({ className }: HomeStatsProps) => {
    const navigate = useNavigate()
    const regionId = useId()
    const titleId = useId()
    const statsListId = useId();
    const { program } = useParams({ strict: false });
    const apiProgram = getApiProgram(program ?? "");
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    // const isWeightLossPlatform = pathname.startsWith("/dashboard/weight-loss");
    const isUrgentCarePlatform = pathname.startsWith("/dashboard/urgent-care");
    const { data: todosData, isLoading: todosLoading } = useQuery(
        { ...todosQueryOptions(apiProgram), refetchInterval: QUICK_ACTIONS_POLLING_INTERVAL }
    );
    const stats = todosData ?? [];

    const handleNavigateToCalendar = () => {
        navigate({ to: "/dashboard/$program/calendar", replace: true, params: { program: program ?? "" } })
    }

    return (
        <section
            className={cn(
                "flex flex-col gap-4 md:gap-6 items-start w-full",
                className
            )}
            aria-labelledby={titleId}
            role="region"
            id={regionId}
        >
            <div
                className="flex w-full justify-between items-center gap-3 sm:gap-10"
                aria-describedby={`${titleId}-description`}
            >
                <div
                    id={titleId}
                    className="text-[#2D2F35] font-medium text-[16px] md:text-xl"
                >
                    {isUrgentCarePlatform ? (
                        <h2 className="flex gap-2 items-center cursor-pointer group">
                            <svg
                                width="20"
                                height="28"
                                viewBox="0 0 20 28"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="transition-transform duration-200 group-hover:-translate-y-1"
                            >
                                <path d="M7.62962 17.3177L6.68147 26.7992L19.0074 13.5251L12.3704 9.73255L13.3185 1.19922L0.992584 14.4733L7.62962 17.3177Z" stroke="#4272DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Home
                        </h2>
                    ) : (
                        <h2 className="flex gap-2">
                            {/* <svg width="20" height="28" viewBox="0 0 20 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M7.62962 17.3177L6.68147 26.7992L19.0074 13.5251L12.3704 9.73255L13.3185 1.19922L0.992584 14.4733L7.62962 17.3177Z" stroke="#4272DD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg> */}
                            Home
                        </h2>
                    )}
                </div>

                <Button
                    variant="outline"
                    className={cn(
                        "transition-colors border !border-[#DCDEE3] text-[#464B55] px-3 md:px-4 py-2 shadow-sm",
                        "button-primary !rounded-full text-xs sm:text-sm font-medium"
                    )}
                    aria-label="View all calendar events"
                    onClick={handleNavigateToCalendar}
                >
                    View My Calendar
                </Button>
            </div>

            {todosLoading ? (
                <QuickActionCardSkeleton />
            ) : (
            <div
                id={statsListId}
                className="flex flex-col sm:flex-row gap-4 md:gap-6 w-full"
                role="group"
                aria-label="Monthly statistics"
            >
                {stats.map((stat, index: number) => (
                    <div
                        key={`stat-${index}`}
                        className="w-full sm:flex-1"
                        role="article"
                        aria-labelledby={`stat-title-${index}`}
                    >
                        <StatsCard
                            title={stat.title}
                            amount={stat.amount}
                            url={stat.url}
                            initial={stat.initial}
                        />
                    </div>
                ))}
            </div>
            )}

            {/* Hidden descriptions for screen readers */}
            <div
                id={`${titleId}-description`}
                className="sr-only"
            >
                Overview of your events and statistics for the current month
            </div>

            {stats.map((stat: DashboardStatsCardProps, index: number) => (
                <div
                    key={`stat-desc-${index}`}
                    id={`stat-title-${index}`}
                    className="sr-only"
                >
                    {stat.title}: {stat.amount} {stat.title.toLowerCase()}
                </div>
            ))}
        </section>
    )
}

export default HomeStats